import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { AppSupportService } from '../../../services/app-support.service';
import { ToastrService } from '../../../services/toastr-service';
import { User } from '../../../models/user.model';
import { UserRequest } from '../../../models/user-request.model';
import { CodeDescription } from '../../../models/code-description.model';
import { MasterDataService } from '../../../services/master-data.service';
import { Shop } from '../../../models/shop.model';
import { ButtonWithConfirmationComponent } from '../../../shared-components/button-with-confirmation/button-with-confirmation.component';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { AuthHelper } from '../../../auth/auth-helper';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-checkbox';
import '@maersk-global/mds-components-core/mc-button';

@Component({
  selector: 'app-create-user',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, ButtonWithConfirmationComponent, AemsMultilineTagInputComponent],
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateUserComponent implements OnInit, OnDestroy {
  userForm!: FormGroup;
  isEditMode = false;
  userId: number | null = null;
  submitted = false;
  activeTab = 0;
  shopCodeRegex: RegExp = /\b[a-zA-Z0-9]{3}\b/g;
  private previousUserData: any = {};

  roleList: CodeDescription[] = [];
  regions: CodeDescription[] = [];

  isShopRoleSelected = true;
  isAdminRoleSelected = false;

  private static normalizeRoleValue(value: string): string {
    return value.trim().toLowerCase();
  }

  private static normalizeRoleToken(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]/g, '');
  }

  private static arrayRequired(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    if (!val || (Array.isArray(val) && val.length === 0)) {
      return { required: true };
    }
    return null;
  }

  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly appSupportService: AppSupportService,
    private readonly toastrService: ToastrService,
    private readonly masterDataService: MasterDataService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setInitialRoleState();

    this.activeTab = this.route.snapshot.queryParamMap.get('tab') === 'non-shop-user' ? 1 : 0;
    this.loadRoles();
    this.loadRegions();
    this.checkEditMode();

    this.userForm.markAsUntouched();
    this.userForm.markAsPristine();
    this.submitted = false;

    // Subscribe to role changes after initial setup to avoid marking form dirty during initialization
    this.userForm
      .get('role')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((role: string) => {
        this.handleRoleChange(role);
      });
  }

  private initializeForm(): void {
    this.userForm = this.fb.group({
      userName: ['', [Validators.required, Validators.maxLength(50)]],
      firstName: ['', [Validators.required, Validators.maxLength(30)]],
      lastName: ['', [Validators.required, Validators.maxLength(30)]],
      emailAddress: ['', [Validators.required, Validators.email, Validators.maxLength(50)]],
      companyName: ['', [Validators.required, Validators.maxLength(50)]],
      locationCode: ['', [Validators.required, Validators.maxLength(8), Validators.pattern(/^[A-Za-z0-9]*$/)]],
      role: ['', Validators.required],
      region: [[]],
      shop: [[]],
      isActive: [true],
      isExternal: [false],
    });
  }

  setInitialRoleState(): void {
    this.isShopRoleSelected = true;
    this.isAdminRoleSelected = false;
    this.userForm.get('shop')?.disable({ emitEvent: false });
  }

  handleRoleChange(selectedRole: string): void {
    if (!selectedRole) {
      this.setInitialRoleState();
      return;
    }

    const isShopRole = this.isShopRoleValue(selectedRole);
    const isAdminRole = this.isAdminRoleValue(selectedRole);
    const wasPristine = this.userForm.pristine;

    if (isShopRole) {
      this.isShopRoleSelected = true;
      this.isAdminRoleSelected = false;
      this.userForm.get('shop')?.enable({ emitEvent: false });
      this.userForm.get('shop')?.setValidators([Validators.required]);
      this.userForm.get('region')?.reset([], { emitEvent: false });
      this.userForm.get('region')?.clearValidators();
      this.userForm.get('region')?.markAsUntouched();
      this.userForm.get('isExternal')?.setValue(true, { emitEvent: false });
    } else {
      this.isShopRoleSelected = false;
      this.isAdminRoleSelected = isAdminRole;
      this.userForm.get('shop')?.disable({ emitEvent: false });
      this.userForm.get('shop')?.clearValidators();
      this.userForm.get('shop')?.reset([], { emitEvent: false });
      this.userForm.get('shop')?.markAsUntouched();
      this.setRegionValidatorsForRole(selectedRole);
      this.userForm.get('isExternal')?.setValue(false, { emitEvent: false });
    }

    this.userForm.get('shop')?.updateValueAndValidity({ emitEvent: false });
    this.userForm.get('region')?.updateValueAndValidity({ emitEvent: false });

    if (wasPristine) {
      this.userForm.markAsPristine();
    }
  }

  isControlInErrorState(control: AbstractControl | null): boolean | null {
    if (control && control.invalid && (control.touched || this.submitted)) {
      return true;
    }
    return null;
  }

  onActiveCheckChange(event: Event): void {
    const checked = (event as CustomEvent).detail?.checked ?? (event.target as HTMLInputElement).checked;
    if (this.userForm.controls['isActive'].value === checked) return;
    this.userForm.controls['isActive'].setValue(checked);
    this.userForm.controls['isActive'].markAsDirty();
    this.cdr.detectChanges();
  }

  private checkEditMode(): void {
    this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.isEditMode = true;
        this.userId = +id;
        this.loadUserDetails(this.userId);
      }
    });
  }

  private loadUserDetails(userId: number): void {
    this.appSupportService
      .getUserDetailsBasedOnUserName(userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (user: User) => {
          this.previousUserData = user;
          const roleValue = this.resolveRoleFromUserDetails(user);
          const isShop = this.isShopRoleValue(roleValue);
          const isAdmin = this.isAdminRoleValue(roleValue);
          this.isShopRoleSelected = isShop;
          this.isAdminRoleSelected = !isShop && isAdmin;

          if (isShop) {
            this.userForm.get('shop')?.enable({ emitEvent: false });
            this.userForm.get('shop')?.setValidators([Validators.required]);
            this.userForm.get('region')?.clearValidators();
          } else {
            this.userForm.get('shop')?.disable({ emitEvent: false });
            this.userForm.get('shop')?.clearValidators();
            this.setRegionValidatorsForRole(roleValue);
          }
          this.userForm.patchValue(
            {
              userName: user.username,
              firstName: user.firstName?.trim(),
              lastName: user.lastName?.trim(),
              emailAddress: user.emailAddress?.trim(),
              companyName: user.companyName?.trim(),
              locationCode: user.locationCode?.trim(),
              role: roleValue,
              region: user.regions?.map((r) => r.code) || [],
              shop: user.maintenanceShops ? user.maintenanceShops.map((s: Shop) => s.maintenanceShopCode) : [],
              isActive: user.isActive,
              isExternal: isShop,
            },
            { emitEvent: false },
          );

          this.userForm.get('shop')?.updateValueAndValidity({ emitEvent: false });
          this.userForm.get('region')?.updateValueAndValidity({ emitEvent: false });

          // In edit mode, role options may load after user details. Re-sync role once options exist.
          this.syncRoleSelectionFromUserDetails();

          this.userForm.get('userName')?.disable({ emitEvent: false });

          // Ensure form is pristine after all initialization
          Promise.resolve().then(() => {
            this.userForm.markAsPristine();
            this.userForm.markAsUntouched();
          });
        },
        error: () => {
          this.toastrService.showToastr('Failed to load user', 'error');
          this.router.navigate(['/users']);
        },
      });
  }

  private loadRoles(): void {
    this.appSupportService
      .getRoles()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.roleList = res || [];
          this.syncRoleSelectionFromUserDetails();
        },
        error: () => {
          this.roleList = [];
          this.toastrService.showToastr('Failed to load roles', 'error');
        },
      });
  }

  private resolveRoleForSelect(rawRole?: string): string {
    if (!rawRole) return '';

    const normalizedRole = CreateUserComponent.normalizeRoleToken(rawRole);
    const matchedRole = this.roleList.find((role) => {
      const normalizedCode = CreateUserComponent.normalizeRoleToken(role.code ?? '');
      const normalizedDescription = CreateUserComponent.normalizeRoleToken(role.description ?? '');

      if (!normalizedCode && !normalizedDescription) {
        return false;
      }

      return (
        normalizedRole === normalizedCode ||
        normalizedRole === normalizedDescription ||
        (normalizedCode.length >= 4 && normalizedRole.includes(normalizedCode)) ||
        (normalizedDescription.length >= 4 && normalizedRole.includes(normalizedDescription))
      );
    });

    return matchedRole?.description ?? rawRole;
  }

  private resolveRoleFromUserDetails(user: User): string {
    const resolvedRoles = (user.roles ?? []).map((role) => this.resolveRoleForSelect(role)).filter(Boolean);

    const resolvedShopRole = resolvedRoles.find((role) => this.isShopRoleValue(role));
    if (resolvedShopRole) {
      return resolvedShopRole;
    }

    if ((user.maintenanceShops?.length ?? 0) > 0) {
      const shopRoleOption = this.roleList.find((role) => this.isShopRoleValue(role.description) || this.isShopRoleValue(role.code));
      if (shopRoleOption?.description) {
        return shopRoleOption.description;
      }
    }

    return resolvedRoles[0] ?? '';
  }

  private isShopRoleValue(role?: string): boolean {
    if (!role) {
      return false;
    }

    const token = CreateUserComponent.normalizeRoleToken(role);
    return token.includes('shop');
  }

  private isAdminRoleValue(role?: string): boolean {
    if (!role) {
      return false;
    }

    const token = CreateUserComponent.normalizeRoleToken(role);
    return token === 'admin' || token === 'roleadmin';
  }

  private setRegionValidatorsForRole(role?: string): void {
    const regionControl = this.userForm.get('region');
    if (!regionControl) {
      return;
    }

    if (this.isAdminRoleValue(role)) {
      regionControl.clearValidators();
      regionControl.markAsUntouched();
      return;
    }

    regionControl.setValidators([CreateUserComponent.arrayRequired]);
  }

  private syncRoleSelectionFromUserDetails(): void {
    if (!this.isEditMode || !this.previousUserData) return;

    const resolvedRole = this.resolveRoleFromUserDetails(this.previousUserData as User);
    if (!resolvedRole) return;

    const roleControl = this.userForm.get('role');
    if (roleControl?.value !== resolvedRole) {
      roleControl?.setValue(resolvedRole, { emitEvent: false });
    }

    this.handleRoleChange(resolvedRole);
  }

  private loadRegions(): void {
    this.masterDataService
      .getRegions()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => (this.regions = res || []),
        error: () => {
          this.regions = [];
          this.toastrService.showToastr('Failed to load regions', 'error');
        },
      });
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const formValue = this.userForm.getRawValue();

    const requestBody: UserRequest = {
      userId: this.isEditMode ? this.userId! : 0,
      username: formValue.userName,
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      emailAddress: formValue.emailAddress,
      companyName: formValue.companyName,
      locationCode: formValue.locationCode,
      roles: formValue.role ? [formValue.role] : [],
      regions: formValue.region ?? [],
      shopCodes: this.parseShopCodes(formValue.shop),
      isActive: formValue.isActive,
      isDeleted: false,
      isExternal: formValue.isExternal,
    };

    const request$ = this.isEditMode ? this.appSupportService.updateUser(requestBody) : this.appSupportService.saveUser(requestBody);

    request$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response: any) => {
        const savedUserId = this.isEditMode ? this.userId! : (response?.userId ?? 0);
        const currentUser = AuthHelper.user;
        const username = formValue.userName;

        this.toastrService.showToastr(
          this.isEditMode ? `User '${username}' updated successfully` : `User '${username}' created successfully`,
          'success',
        );

        const previousUserRequest: UserRequest = {
          userId: this.previousUserData?.userId ?? 0,
          username: this.previousUserData?.username ?? '',
          firstName: this.previousUserData?.firstName ?? '',
          lastName: this.previousUserData?.lastName ?? '',
          emailAddress: this.previousUserData?.emailAddress ?? '',
          companyName: this.previousUserData?.companyName ?? '',
          locationCode: this.previousUserData?.locationCode ?? '',
          roles: this.previousUserData?.roles ?? [],
          regions: this.previousUserData?.regions?.map((r: any) => r.code ?? r) ?? [],
          shopCodes: this.previousUserData?.maintenanceShops?.map((s: any) => s.maintenanceShopCode ?? s) ?? [],
          isActive: this.previousUserData?.isActive ?? true,
          isDeleted: this.previousUserData?.isDeleted ?? false,
          isExternal: this.previousUserData?.isExternal ?? false,
        };

        // Audit logging is now handled server-side. Do not post audit entries from the frontend.
        // Previously we posted an audit payload here via `postMasterAuditLog(...)`.
        // Navigate immediately after successful save/update.
        void this.router.navigate(this.getListNavigation(), { queryParams: this.getListQueryParams() });
      },
      error: (err) => {
        const message = err?.error?.errorMessage || 'Something went wrong';
        this.toastrService.showToastr(message, 'error');
      },
    });
  }

  private getListNavigation(): any[] {
    return ['/users'];
  }

  private getListQueryParams(): object {
    return { tab: this.activeTab === 1 ? 'non-shop-user' : 'shop-user' };
  }

  private parseShopCodes(shop: string | string[] | null): string[] {
    if (!shop) {
      return [];
    }
    if (Array.isArray(shop)) {
      return shop;
    }
    return shop
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean);
  }

  onCancel(): () => void {
    return () => {
      if (this.isEditMode) {
        this.router.navigate(['/users', this.userId], {
          queryParams: { tab: this.activeTab === 1 ? 'non-shop-user' : 'shop-user' },
        });
      } else {
        this.router.navigate(this.getListNavigation(), { queryParams: this.getListQueryParams() });
      }
    };
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
